import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  NavigateNext as NavigateNextIcon,
  InsertDriveFile as FileIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/authStore';
import api from '../utils/api';
import { formatBytes, formatDate } from '../utils/formatters';
import AIModelSelect, { DEFAULT_MODELS } from '../components/common/AIModelSelect';

const MODEL_OPTIONS = {
  local: [
    { value: 'all-MiniLM-L6-v2', label: 'all-MiniLM-L6-v2 (384d)' },
    { value: 'all-mpnet-base-v2', label: 'all-mpnet-base-v2 (768d)' },
  ],
  openai: [
    { value: 'text-embedding-3-small', label: 'text-embedding-3-small (1536d)' },
    { value: 'text-embedding-3-large', label: 'text-embedding-3-large (3072d)' },
  ],
  // Ollama embedding models are populated dynamically, but keep static defaults
  // for the dimension map in case the API is unreachable.
  ollama: [
    { value: 'nomic-embed-text', label: 'nomic-embed-text (768d)' },
    { value: 'mxbai-embed-large', label: 'mxbai-embed-large (1024d)' },
    { value: 'all-minilm', label: 'all-minilm (384d)' },
  ],
};

const DIMENSION_MAP = {
  'all-MiniLM-L6-v2': 384,
  'all-mpnet-base-v2': 768,
  'text-embedding-3-small': 1536,
  'text-embedding-3-large': 3072,
  // Ollama embedding models
  'nomic-embed-text': 768,
  'mxbai-embed-large': 1024,
  'all-minilm': 384,
  'snowflake-arctic-embed': 1024,
};

// Patterns that identify embedding models in Ollama (matched against base name)
const OLLAMA_EMBEDDING_PATTERNS = [
  'embed', 'bge-', 'bge_', 'all-minilm', 'e5-', 'gte-',
];

const STATUS_COLORS = {
  pending: 'default',
  processing: 'info',
  ready: 'success',
  failed: 'error',
};

const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, checkAuth } = useAuth();
  const fileInputRef = useRef(null);

  // View state
  const [selectedCollection, setSelectedCollection] = useState(null);

  // Collections list state
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Create collection dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    embedding_provider: 'local',
    embedding_model: 'all-MiniLM-L6-v2',
    embedding_dimensions: 384,
    chunk_size: 512,
    chunk_overlap: 50,
  });

  // Delete collection dialog state
  const [deleteCollectionDialogOpen, setDeleteCollectionDialogOpen] = useState(false);
  const [deletingCollection, setDeletingCollection] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);

  // Delete document dialog state
  const [deleteDocDialogOpen, setDeleteDocDialogOpen] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTopK, setSearchTopK] = useState(5);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Chat (RAG) state
  const [queryMode, setQueryMode] = useState('snippets');
  const [chatAnswer, setChatAnswer] = useState(null);
  const [chatSources, setChatSources] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatProvider, setChatProvider] = useState('openai');
  const [chatModel, setChatModel] = useState('gpt-5-mini');

  // Ollama state
  const [ollamaAvailable, setOllamaAvailable] = useState(false);
  const [ollamaModelsLoading, setOllamaModelsLoading] = useState(false);
  const [ollamaEmbeddingModels, setOllamaEmbeddingModels] = useState([]);
  const [ollamaPulling, setOllamaPulling] = useState(false);

  // Fetch collections
  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/knowledge-base/');
      setCollections(response.data.collections || []);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError(err.message || 'Failed to load collections.');
      if (err.response?.status === 401) {
        navigate('/login', { state: { from: '/knowledge-base' } });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Fetch documents for selected collection
  const fetchDocuments = useCallback(async () => {
    if (!selectedCollection) return;
    try {
      setDocumentsLoading(true);
      const response = await api.get(`/knowledge-base/${selectedCollection.id}/documents`);
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.message || 'Failed to load documents.');
    } finally {
      setDocumentsLoading(false);
    }
  }, [selectedCollection]);

  // Fetch Ollama models
  const fetchOllamaModels = useCallback(async () => {
    setOllamaModelsLoading(true);
    try {
      const response = await api.get('/ollama/models');
      const models = response.data.models || [];
      setOllamaAvailable(true);

      // Separate out embedding models (by name pattern)
      const embModels = models.filter((m) => {
        const baseName = m.name.split(':')[0].toLowerCase();
        return OLLAMA_EMBEDDING_PATTERNS.some((pat) => baseName.includes(pat));
      });
      setOllamaEmbeddingModels(embModels);
    } catch (err) {
      console.warn('Ollama not available:', err.message);
      setOllamaAvailable(false);
      setOllamaEmbeddingModels([]);
    } finally {
      setOllamaModelsLoading(false);
    }
  }, []);

  // Pull an Ollama model
  const handleOllamaPull = async (modelName) => {
    setOllamaPulling(true);
    try {
      await api.post('/ollama/pull', { name: modelName });
      await fetchOllamaModels();
    } catch (err) {
      console.error('Error pulling Ollama model:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to pull model.');
    } finally {
      setOllamaPulling(false);
    }
  };

  // Initial auth check and data load
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      if (!isMounted) return;

      try {
        if (!isAuthenticated) {
          const authValid = await checkAuth();
          if (!authValid) {
            navigate('/login', { state: { from: '/knowledge-base' } });
            return;
          }
        }

        if (isAuthenticated) {
          await fetchCollections();
          // Fire-and-forget: check Ollama availability in background
          fetchOllamaModels();
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error in initial data load:', err);
        setError(err.message || 'An error occurred while loading data');
        if (err.response?.status === 401) {
          navigate('/login', { state: { from: '/knowledge-base' } });
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, checkAuth, navigate, fetchCollections, fetchOllamaModels]);

  // Fetch documents when a collection is selected
  useEffect(() => {
    if (selectedCollection) {
      fetchDocuments();
    }
  }, [selectedCollection, fetchDocuments]);

  // Auto-refresh polling when documents are pending or processing
  useEffect(() => {
    if (!selectedCollection) return;

    const hasPendingDocs = documents.some(
      (doc) => doc.status === 'pending' || doc.status === 'processing'
    );

    if (!hasPendingDocs) return;

    const interval = setInterval(() => {
      fetchDocuments();
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedCollection, documents, fetchDocuments]);

  // -- Create collection --
  const handleCreateDialogOpen = () => {
    setCreateDialogOpen(true);
    setNewCollection({
      name: '',
      description: '',
      embedding_provider: 'local',
      embedding_model: 'all-MiniLM-L6-v2',
      embedding_dimensions: 384,
      chunk_size: 512,
      chunk_overlap: 50,
    });
  };

  const handleCreateDialogClose = () => {
    if (creating) return;
    setCreateDialogOpen(false);
  };

  const handleProviderChange = (event) => {
    const provider = event.target.value;
    if (provider === 'ollama') {
      // For Ollama, use dynamically fetched embedding models if available,
      // otherwise fall back to static defaults
      const availableModels = ollamaEmbeddingModels.length > 0
        ? ollamaEmbeddingModels.map((m) => m.name)
        : MODEL_OPTIONS.ollama.map((m) => m.value);
      const defaultModel = availableModels[0] || 'nomic-embed-text';
      const baseName = defaultModel.split(':')[0];
      setNewCollection((prev) => ({
        ...prev,
        embedding_provider: provider,
        embedding_model: defaultModel,
        embedding_dimensions: DIMENSION_MAP[baseName] || 768,
      }));
    } else {
      const defaultModel = MODEL_OPTIONS[provider][0].value;
      setNewCollection((prev) => ({
        ...prev,
        embedding_provider: provider,
        embedding_model: defaultModel,
        embedding_dimensions: DIMENSION_MAP[defaultModel],
      }));
    }
  };

  const handleModelChange = (event) => {
    const model = event.target.value;
    const baseName = model.split(':')[0];
    setNewCollection((prev) => ({
      ...prev,
      embedding_model: model,
      embedding_dimensions: DIMENSION_MAP[baseName] || DIMENSION_MAP[model] || 768,
    }));
  };

  const handleCreateCollection = async () => {
    if (!newCollection.name.trim()) return;
    setCreating(true);

    try {
      await api.post('/knowledge-base/', newCollection);
      setCreateDialogOpen(false);
      await fetchCollections();
    } catch (err) {
      console.error('Error creating collection:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create collection.');
    } finally {
      setCreating(false);
    }
  };

  // -- Delete collection --
  const handleDeleteCollectionClick = (collection) => {
    setCollectionToDelete(collection);
    setDeleteCollectionDialogOpen(true);
  };

  const handleDeleteCollection = async () => {
    if (!collectionToDelete) return;
    setDeletingCollection(true);

    try {
      await api.delete(`/knowledge-base/${collectionToDelete.id}`);
      setDeleteCollectionDialogOpen(false);
      setCollectionToDelete(null);
      if (selectedCollection?.id === collectionToDelete.id) {
        setSelectedCollection(null);
        setDocuments([]);
      }
      await fetchCollections();
    } catch (err) {
      console.error('Error deleting collection:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to delete collection.');
    } finally {
      setDeletingCollection(false);
    }
  };

  // -- Upload document --
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedCollection) return;

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/knowledge-base/${selectedCollection.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to upload document.');
    } finally {
      setUploading(false);
      // Reset the file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // -- Delete document --
  const handleDeleteDocClick = (doc) => {
    setDocToDelete(doc);
    setDeleteDocDialogOpen(true);
  };

  const handleDeleteDoc = async () => {
    if (!docToDelete || !selectedCollection) return;
    setDeletingDoc(true);

    try {
      await api.delete(`/knowledge-base/${selectedCollection.id}/documents/${docToDelete.id}`);
      setDeleteDocDialogOpen(false);
      setDocToDelete(null);
      await fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to delete document.');
    } finally {
      setDeletingDoc(false);
    }
  };

  // -- Reprocess document --
  const handleReprocessDoc = async (doc) => {
    if (!selectedCollection) return;

    try {
      await api.post(`/knowledge-base/${selectedCollection.id}/documents/${doc.id}/reprocess`);
      await fetchDocuments();
    } catch (err) {
      console.error('Error reprocessing document:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to reprocess document.');
    }
  };

  // -- Search collection --
  const handleSearch = async () => {
    if (!searchQuery.trim() || !selectedCollection) return;

    if (queryMode === 'chat') {
      return handleChat();
    }

    setSearching(true);
    setSearchError('');
    setSearchResults(null);

    try {
      const response = await api.post(`/knowledge-base/${selectedCollection.id}/search`, {
        query: searchQuery,
        top_k: searchTopK,
      });
      setSearchResults(response.data);
    } catch (err) {
      console.error('Error searching collection:', err);
      setSearchError(err.response?.data?.detail || err.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  // -- Chat (RAG) query --
  const handleChat = async () => {
    if (!searchQuery.trim() || !selectedCollection) return;
    setChatLoading(true);
    setChatError('');
    setChatAnswer(null);
    setChatSources([]);

    try {
      const response = await api.post(`/knowledge-base/${selectedCollection.id}/chat`, {
        query: searchQuery,
        top_k: searchTopK,
        provider: chatProvider,
        model: chatModel,
      });
      setChatAnswer(response.data.answer);
      setChatSources(response.data.sources || []);
    } catch (err) {
      console.error('Error in chat query:', err);
      setChatError(err.response?.data?.detail || err.message || 'Chat query failed.');
    } finally {
      setChatLoading(false);
    }
  };

  // -- Navigate to collection detail --
  const handleCollectionClick = (collection) => {
    setSelectedCollection(collection);
    setDocuments([]);
  };

  // -- Navigate back to collections list --
  const handleBackToCollections = () => {
    setSelectedCollection(null);
    setDocuments([]);
    setError('');
    setSearchQuery('');
    setSearchResults(null);
    setSearchError('');
    setQueryMode('snippets');
    setChatAnswer(null);
    setChatSources([]);
    setChatError('');
    setChatProvider('openai');
    setChatModel(DEFAULT_MODELS.openai);
  };

  // ========== RENDER: Collection Detail View ==========
  if (selectedCollection) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumb */}
        <Paper sx={{ px: 2, py: 1, mb: 2, display: 'flex', alignItems: 'center' }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <MuiLink
              component="button"
              variant="body2"
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={handleBackToCollections}
              color="inherit"
            >
              <MenuBookIcon sx={{ mr: 0.5 }} fontSize="small" />
              Knowledge Base
            </MuiLink>
            <Typography variant="body2" color="text.primary">
              {selectedCollection.name}
            </Typography>
          </Breadcrumbs>
        </Paper>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {selectedCollection.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </Box>
        </Box>

        {/* Collection info summary */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Provider</Typography>
              <Typography variant="body2">
                {selectedCollection.embedding_provider === 'local'
                  ? 'Local (sentence-transformers)'
                  : selectedCollection.embedding_provider === 'ollama'
                  ? 'Ollama'
                  : 'OpenAI'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Model</Typography>
              <Typography variant="body2">{selectedCollection.embedding_model}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.25 }}>
                <Chip
                  label={selectedCollection.status || 'ready'}
                  size="small"
                  color={STATUS_COLORS[selectedCollection.status] || 'success'}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Documents</Typography>
              <Typography variant="body2">{selectedCollection.document_count ?? documents.length}</Typography>
            </Box>
            {selectedCollection.description && (
              <Box>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{selectedCollection.description}</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Documents table */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Documents
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Content Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Chunks</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Box sx={{ opacity: 0.5 }}>
                        <FileIcon sx={{ fontSize: 48, mb: 1 }} />
                        <Typography>
                          No documents yet. Upload a document to get started.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">{doc.filename || doc.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{doc.content_type || 'Unknown'}</TableCell>
                      <TableCell>{formatBytes(doc.file_size || doc.size)}</TableCell>
                      <TableCell>
                        <Chip
                          label={doc.status}
                          size="small"
                          color={STATUS_COLORS[doc.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>{doc.chunk_count ?? '--'}</TableCell>
                      <TableCell>{formatDate(doc.created_at || doc.uploaded_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Reprocess">
                          <IconButton
                            size="small"
                            onClick={() => handleReprocessDoc(doc)}
                            disabled={doc.status === 'processing'}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDocClick(doc)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Search / Chat panel */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Test Query
            </Typography>
            <ToggleButtonGroup
              value={queryMode}
              exclusive
              onChange={(e, val) => val && setQueryMode(val)}
              size="small"
            >
              <ToggleButton value="snippets">
                <SearchIcon sx={{ mr: 0.5 }} fontSize="small" />
                Snippets
              </ToggleButton>
              <ToggleButton value="chat">
                <ChatIcon sx={{ mr: 0.5 }} fontSize="small" />
                Chat
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Chat provider & model selection (visible in chat mode) */}
          {queryMode === 'chat' && (
            <Box sx={{ mb: 2 }}>
              <AIModelSelect
                provider={chatProvider}
                model={chatModel}
                onProviderChange={setChatProvider}
                onModelChange={setChatModel}
                disabled={chatLoading}
                providerLabel="LLM Provider"
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
            <TextField
              label={queryMode === 'chat' ? 'Ask a question' : 'Search query'}
              fullWidth
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              disabled={searching || chatLoading}
              size="small"
            />
            <TextField
              label="Top K"
              type="number"
              variant="outlined"
              value={searchTopK}
              onChange={(e) => setSearchTopK(Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)))}
              disabled={searching || chatLoading}
              size="small"
              sx={{ width: 100 }}
              inputProps={{ min: 1, max: 50 }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={(searching || chatLoading) || !searchQuery.trim()}
              startIcon={(searching || chatLoading) ? <CircularProgress size={20} /> : queryMode === 'chat' ? <ChatIcon /> : <SearchIcon />}
              sx={{ minWidth: 110 }}
            >
              {searching ? 'Searching...' : chatLoading ? 'Asking...' : queryMode === 'chat' ? 'Ask' : 'Search'}
            </Button>
          </Box>

          {searchError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSearchError('')}>
              {searchError}
            </Alert>
          )}

          {chatError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setChatError('')}>
              {chatError}
            </Alert>
          )}

          {/* Chat mode results */}
          {queryMode === 'chat' && chatAnswer && (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 2,
                  bgcolor: 'background.default',
                  borderLeft: 4,
                  borderLeftColor: 'primary.main',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {chatAnswer}
                </Typography>
              </Paper>

              {chatSources.length > 0 && (
                <Accordion variant="outlined" defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" color="text.secondary">
                      Sources ({chatSources.length} chunks used)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1 }}>
                    {chatSources.map((result, index) => (
                      <Paper key={result.chunk_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
                            <Typography variant="body2" fontWeight={500}>
                              {result.document_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              chunk {result.chunk_index}
                            </Typography>
                          </Box>
                          <Chip
                            label={`Score: ${(result.score * 100).toFixed(1)}%`}
                            size="small"
                            color={result.score > 0.7 ? 'success' : result.score > 0.4 ? 'warning' : 'default'}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            bgcolor: 'grey.50',
                            p: 1.5,
                            borderRadius: 1,
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          {result.content}
                        </Typography>
                      </Paper>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}

          {/* Snippets mode results */}
          {queryMode === 'snippets' && searchResults && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {searchResults.results?.length || 0} result{searchResults.results?.length !== 1 ? 's' : ''} for &quot;{searchResults.query}&quot;
              </Typography>
              {searchResults.results?.length === 0 ? (
                <Alert severity="info">No results found. Make sure documents have been processed.</Alert>
              ) : (
                searchResults.results?.map((result, index) => (
                  <Paper key={result.chunk_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
                        <Typography variant="body2" fontWeight={500}>
                          {result.document_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          chunk {result.chunk_index}
                        </Typography>
                      </Box>
                      <Chip
                        label={`Score: ${(result.score * 100).toFixed(1)}%`}
                        size="small"
                        color={result.score > 0.7 ? 'success' : result.score > 0.4 ? 'warning' : 'default'}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        bgcolor: 'grey.50',
                        p: 1.5,
                        borderRadius: 1,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {result.content}
                    </Typography>
                  </Paper>
                ))
              )}
            </Box>
          )}
        </Paper>

        {/* Delete document confirmation dialog */}
        <Dialog
          open={deleteDocDialogOpen}
          onClose={() => !deletingDoc && setDeleteDocDialogOpen(false)}
        >
          <DialogTitle>Delete Document</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete &quot;{docToDelete?.filename || docToDelete?.name}&quot;? This will remove the document and all its chunks. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDocDialogOpen(false)}
              disabled={deletingDoc}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteDoc}
              color="error"
              disabled={deletingDoc}
              startIcon={deletingDoc ? <CircularProgress size={20} /> : null}
            >
              {deletingDoc ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // ========== RENDER: Collections List View ==========
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Knowledge Base
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateDialogOpen}
        >
          New Collection
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : collections.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box sx={{ opacity: 0.5 }}>
                      <MenuBookIcon sx={{ fontSize: 48, mb: 1 }} />
                      <Typography>
                        No collections yet. Create a collection to get started.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                collections.map((collection) => (
                  <TableRow
                    key={collection.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleCollectionClick(collection)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {collection.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {collection.description || '--'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {collection.embedding_provider === 'local'
                        ? 'Local'
                        : collection.embedding_provider === 'ollama'
                        ? 'Ollama'
                        : 'OpenAI'}
                    </TableCell>
                    <TableCell>{collection.embedding_model}</TableCell>
                    <TableCell>{collection.document_count ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={collection.status || 'ready'}
                        size="small"
                        color={STATUS_COLORS[collection.status] || 'success'}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCollectionClick(collection)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Collection dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Collection</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            required
            value={newCollection.name}
            onChange={(e) => setNewCollection((prev) => ({ ...prev, name: e.target.value }))}
            disabled={creating}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={newCollection.description}
            onChange={(e) => setNewCollection((prev) => ({ ...prev, description: e.target.value }))}
            disabled={creating}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="provider-label">Embedding Provider</InputLabel>
            <Select
              labelId="provider-label"
              value={newCollection.embedding_provider}
              label="Embedding Provider"
              onChange={handleProviderChange}
              disabled={creating}
            >
              <MenuItem value="local">Local (sentence-transformers)</MenuItem>
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="ollama" disabled={!ollamaAvailable}>
                Ollama {!ollamaAvailable ? '(not available)' : ''}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="model-label">Model</InputLabel>
            <Select
              labelId="model-label"
              value={newCollection.embedding_model}
              label="Model"
              onChange={handleModelChange}
              disabled={creating || (newCollection.embedding_provider === 'ollama' && ollamaModelsLoading)}
            >
              {newCollection.embedding_provider === 'ollama' ? (
                // Show dynamically fetched Ollama embedding models, or static defaults
                (ollamaEmbeddingModels.length > 0
                  ? ollamaEmbeddingModels.map((m) => (
                      <MenuItem key={m.name} value={m.name}>
                        {m.name}
                      </MenuItem>
                    ))
                  : MODEL_OPTIONS.ollama.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))
                )
              ) : (
                MODEL_OPTIONS[newCollection.embedding_provider].map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {newCollection.embedding_provider === 'ollama' && ollamaEmbeddingModels.length === 0 && ollamaAvailable && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No embedding models found in Ollama. You can pull one (e.g. nomic-embed-text) using the button below.
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={ollamaPulling}
                  onClick={() => handleOllamaPull('nomic-embed-text')}
                  startIcon={ollamaPulling ? <CircularProgress size={16} /> : null}
                >
                  {ollamaPulling ? 'Pulling...' : 'Pull nomic-embed-text'}
                </Button>
              </Box>
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              margin="dense"
              label="Chunk Size"
              type="number"
              variant="outlined"
              value={newCollection.chunk_size}
              onChange={(e) =>
                setNewCollection((prev) => ({
                  ...prev,
                  chunk_size: parseInt(e.target.value, 10) || 0,
                }))
              }
              disabled={creating}
              sx={{ flex: 1 }}
              inputProps={{ min: 1 }}
            />
            <TextField
              margin="dense"
              label="Chunk Overlap"
              type="number"
              variant="outlined"
              value={newCollection.chunk_overlap}
              onChange={(e) =>
                setNewCollection((prev) => ({
                  ...prev,
                  chunk_overlap: parseInt(e.target.value, 10) || 0,
                }))
              }
              disabled={creating}
              sx={{ flex: 1 }}
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateCollection}
            variant="contained"
            disabled={creating || !newCollection.name.trim()}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete collection confirmation dialog */}
      <Dialog
        open={deleteCollectionDialogOpen}
        onClose={() => !deletingCollection && setDeleteCollectionDialogOpen(false)}
      >
        <DialogTitle>Delete Collection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{collectionToDelete?.name}&quot;? This will remove the collection and all its documents. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteCollectionDialogOpen(false)}
            disabled={deletingCollection}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCollection}
            color="error"
            disabled={deletingCollection}
            startIcon={deletingCollection ? <CircularProgress size={20} /> : null}
          >
            {deletingCollection ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KnowledgeBasePage;
